import { History, User, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type ActivityView = "history" | "user" | "settings" | null;

interface ActivityBarProps {
  activeView: ActivityView;
  onViewChange: (view: ActivityView) => void;
}

export function ActivityBar({ activeView, onViewChange }: ActivityBarProps) {
  const topActivities = [
    { id: "history" as const, icon: History, label: "History" },
  ];

  const bottomActivities = [
    { id: "user" as const, icon: User, label: "User Profile" },
    { id: "settings" as const, icon: Settings, label: "Settings" },
  ];

  const handleClick = (id: ActivityView) => {
    // Toggle: if clicking the same view, close the panel
    onViewChange(activeView === id ? null : id);
  };

  const renderActivity = (activity: { id: ActivityView; icon: React.ComponentType<any>; label: string }) => {
    const Icon = activity.icon;
    const isActive = activeView === activity.id;
    
    return (
      <Tooltip key={activity.id}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleClick(activity.id)}
            className={`w-9 h-9 ${
              isActive 
                ? "bg-accent text-accent-foreground" 
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{activity.label}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div className="w-12 h-full bg-card border-r flex flex-col items-center py-2 justify-between">
      <TooltipProvider>
        <div className="flex flex-col gap-1">
          {topActivities.map(renderActivity)}
        </div>
        <div className="flex flex-col gap-1">
          {bottomActivities.map(renderActivity)}
        </div>
      </TooltipProvider>
    </div>
  );
}
